// src/components/Testimonials.tsx
'use client';

import { useEffect, useState, useRef } from 'react';

type Testimonial = {
  quote: string;
  name: string;
  subtitle?: string;
};

const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'I LOVE FeelGood! The platform is intuitive, and the team was upfront about all testing costs. I felt completely supported as I navigated my results, and the app makes tracking progress easy.',
    name: 'Kelly P.',
    subtitle: 'FeelGood made health transparent!',
  },
  {
    quote:
      'The insights were clear and easy to understand. The personalized plan based on those biometrics was a game-changer. I never felt rushed and I always had someone to message about my progress or any side effects from new routines.',
    name: 'Sarah H.',
    subtitle: 'Optimized my health in 90 days',
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);

  const touchStartX = useRef<number | null>(null);
  const touchCurrentX = useRef<number | null>(null);

  const SWIPE_THRESHOLD = 40; // pixels

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (
      touchStartX.current === null ||
      touchCurrentX.current === null
    ) {
      touchStartX.current = null;
      touchCurrentX.current = null;
      return;
    }

    const diff = touchStartX.current - touchCurrentX.current;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) {
        // swipe left → next
        next();
      } else {
        // swipe right → prev
        prev();
      }
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
  };


  const next = () => setIndex((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () =>
    setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  // simple autoplay
  useEffect(() => {
    const id = setInterval(next, 9000);
    return () => clearInterval(id);
  }, []);

  const current = TESTIMONIALS[index];

  return (
    <section id="testimonials" className="bg-[#f7f5f0] py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <p className="text-center text-xs font-semibold tracking-[.25em] text-[#0f2534]/70 uppercase">
          Real users. Real results. Real support.
        </p>
        <h2 className="mt-3 text-center text-3xl sm:text-4xl font-semibold text-[#0f2534]">
          Hear From Our Clients
        </h2>

        {/* Slider shell */}
        <div
          className="mt-10 relative"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Card */}
          <article className="rounded-3xl bg-white shadow-[0_30px_80px_rgba(15,37,52,0.08)] px-6 sm:px-10 py-8 sm:py-10">
            <p className="text-[15px] sm:text-[16px] leading-7 text-[#0f2534]/90">
              “{current.quote}”
            </p>

            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-semibold text-[#0f2534]">
                  {current.name}
                </p>
                {current.subtitle && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {current.subtitle}
                  </p>
                )}
              </div>

              {/* Stars */}
              <div className="flex items-center gap-1 text-[#ff7500]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg
                    key={i}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="h-4 w-4"
                    fill="currentColor"
                  >
                    <path d="m12 17.3-6.16 3.64 1.64-6.98L2 8.98l7.1-.6L12 1.8l2.9 6.58 7.1.6-5.48 5.98 1.64 6.98z" />
                  </svg>
                ))}
              </div>
            </div>
          </article>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous testimonial"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#0f2534] shadow-sm hover:border-[#ff7500] hover:text-[#ff7500] transition"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M15 5 8 12l7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next testimonial"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#0f2534] shadow-sm hover:border-[#ff7500] hover:text-[#ff7500] transition"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === index
                      ? 'w-6 bg-[#ff7500]'
                      : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Show testimonial ${i + 1}`}
                  aria-current={i === index}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
